import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { hiddenContentClassName } from "@1js/acui-common";
import { useButtonStyles_unstable } from "@fluentui/react-components";

import type { AppGalleryButtonState } from "./AppGalleryButton.type";
// eslint-disable-next-line no-restricted-imports
import {
  hiddenContentStyle,
  useCommonDisabledStyles,
} from "@1js/acui-common/lib/styling/next";
import { customTokens } from "@1js/acui-theme";
// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastButtonRestStyles,
  commonHighContrastColorButtonRest,
  HighContrastSelector,
  commonHighContrastHoverBorderStyles,
  commonMediumContrastBackgroundHoverFocus,
  commonHighContrastBackgroundHoverFocus,
  useSelectedIconHighContrastStyles,
  useHoveredIconHighContrastStyles,
} from "@1js/acui-common/lib/styling/next";
import type { GriffelStyle } from "@fluentui/react-components";
import { getHighContrastNoAdjustStyle } from "@fluentui/style-utilities";

const commonSelectedButtonStyles: GriffelStyle = {
  outline: `1px solid ${customTokens.colorNeutralStrokeAccessible}`,
  outlineOffset: "-1px",
};

const commonSelectedButtonHighContrastStyles: GriffelStyle = {
  ...commonHighContrastBackgroundHoverFocus,
  forcedColorAdjust: "none",
  ...commonHighContrastHoverBorderStyles,
};

const commonSelectedButtonHoverFocusActiveHighContrastStyles: GriffelStyle = {
  outlineOffset: "0px",
  forcedColorAdjust: "none",
  ...commonHighContrastHoverBorderStyles,
};

const useRootStyles = makeStyles({
  root: {
    [`& .${hiddenContentClassName}`]: {
      ...hiddenContentStyle,
    },
    minWidth: "unset",
    fontSize: customTokens.fontSizeBase200,
    fontWeight: customTokens.fontWeightRegular,
    lineHeight: customTokens.lineHeightBase200,
    "&:focus": {
      outline: `1px solid ${customTokens.colorNeutralStrokeAccessible}`,
      outlineOffset: "-1px",
    },
    [`${HighContrastSelector}`]: {
      ...(getHighContrastNoAdjustStyle() as GriffelStyle),
      ...commonHighContrastButtonRestStyles,
      ...commonHighContrastColorButtonRest,
      outlineStyle: "none",
      border: `${customTokens.strokeWidthThin} solid ButtonText`,
      "&:active, &:focus, &:hover": {
        ...commonMediumContrastBackgroundHoverFocus,
        forcedColorAdjust: "none",
        ...commonHighContrastHoverBorderStyles,
        outlineOffset: "0px",
      },
    },
  },
  selected: {
    ...commonSelectedButtonStyles,
    [HighContrastSelector]: {
      ...commonSelectedButtonHighContrastStyles,
      "&:active, &:focus, &:hover": {
        ...commonSelectedButtonHoverFocusActiveHighContrastStyles,
        ...commonMediumContrastBackgroundHoverFocus,
        ...useHoveredIconHighContrastStyles,
      },
    },
  },
  disabledAndSelected: {
    ...commonSelectedButtonStyles,
    [HighContrastSelector]: {
      ...commonSelectedButtonHighContrastStyles,
      ...useSelectedIconHighContrastStyles,
      "&:active, &:focus, &:hover": {
        ...commonSelectedButtonHoverFocusActiveHighContrastStyles,
        ...commonHighContrastBackgroundHoverFocus,
        ...useSelectedIconHighContrastStyles,
      },
    },
  },
});

const useSizeStyles = makeStyles({
  // gallery button can't be small
  medium: {
    height: "26px",
    margin: customTokens.spacingVerticalXXS,
    padding: customTokens.spacingVerticalXS,
  },
  large: {
    height: "52px",
    margin: customTokens.spacingVerticalXS,
    padding: customTokens.spacingVerticalXS,
  },
  icon: {
    height: "auto",
    margin: customTokens.spacingVerticalNone,
    width: "auto",
  },
  iconSlr: {
    margin: customTokens.spacingVerticalNone,
    width: "fit-content",
    minWidth: "26px",
  },
});

export const useAppGalleryButtonStyles = (
  state: AppGalleryButtonState
): AppGalleryButtonState => {
  const rootStyles = useRootStyles();
  const sizeStyles = useSizeStyles();
  const disabledStyles = useCommonDisabledStyles();

  state.root.className = mergeClasses(
    "ms-ButtonNext",
    "ms-Button",
    rootStyles.root,
    state.size === "large" ? sizeStyles.large : sizeStyles.medium,
    state.disabled && state.selected && rootStyles.disabledAndSelected,
    (state.disabled || state.disabledFocusable) &&
      !state.selected &&
      disabledStyles.base,
    state.selected && !state.disabled && rootStyles.selected,
    state.root.className
  );
  if (state.icon) {
    state.icon.className = mergeClasses(
      state.size === "large" ? sizeStyles.icon : sizeStyles.iconSlr
    );
  }
  useButtonStyles_unstable(state);
  return state;
};
